import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowDownIcon from "@tCentro/Icons/ArrowDownIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { FC, useContext, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Default badge graphic (generic "official distributor" stack-of-photos
// image), hosted on the CMS's asset CDN — same pattern as brand.logoLargeImagePath,
// so no bundler import needed. Used whenever a brand doesn't override
// fields.badgeImage.
const DEFAULT_BADGE_IMAGE = "/userfiles/images/group%20556.png?width=85&height=77";

const CONTENT_TRUNCATE_LENGTH = 450;

const enum fields {
    getInTouchCheckbox = "getInTouchCheckbox",
    getInTouchUrl = "getInTouchUrl",
    badgeLabel = "badgeLabel",
    badgeDetail = "badgeDetail",
    badgeImage = "badgeImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.getInTouchCheckbox]: boolean;
        [fields.getInTouchUrl]: LinkFieldValue;
        [fields.badgeLabel]: string;
        [fields.badgeDetail]: string;
        // Optional per-brand override (CMS image picker). If left blank, the
        // default distributor-badge graphic (DEFAULT_BADGE_IMAGE) is used.
        [fields.badgeImage]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const currentPage = getCurrentPage(state);
    const pageWidgets = getWidgetsByPageId(state, currentPage.id);
    const isBrandDetailsCategoriesPresent = pageWidgets.some(
        (widget: WidgetProps) => widget.type === "BrandDetails/BrandDetailsCategories",
    );

    return {
        isBrandDetailsCategoriesPresent,
    };
};

export interface BrandDetailsLogoStyle {
    brandImageGridItem?: GridItemProps;
    imageWrapper?: InjectableCss;
    brandContentGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    button?: InjectableCss;
    brandName?: InjectableCss;
    extButton?: LinkPresentationProps;
    brandSubheadingStyles?: InjectableCss;
    brandGridContainerStyles?: GridContainerProps;
    brandContentWrapper?: InjectableCss;
    brandContentContainerStyles?: InjectableCss;
    badgeWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    linkStyles?: InjectableCss;
    jumpToCategories?: LinkPresentationProps;
    clampedContentCss?: InjectableCss;
}

export const logoStyles: BrandDetailsLogoStyle = {
    clampedContentCss: {
        css: css`
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 22px;
            color: #4a4a4a;
            // margin-bottom: 16px;

            a {
                color: rgb(15, 150, 217);
                text-decoration: none;
            }
        `,
    },
    brandContentWrapper: {
        css: css`
            @media (min-width: 1024px) {
                width: 82%;
            }
            margin-bottom: 20px;
        `,
    },
    badgeWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 20px;
            width: 100%;
            background-color: #eef3ee;

            padding: 13px 8px;
            margin-top: 15px;
            max-height: 42px;

            svg {
                flex-shrink: 0;
            }

            .badge-text {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .badge-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 12px;
                font-weight: 600 !important;
                color: ${COLORS.primary.dark};
                line-height: 16px;
            }

            .badge-detail {
                display: block;
                font-size: 14px;
                font-weight: 600 !important;
                color: ${COLORS.primary.dark};
                line-height: 16px;
            }
        `,
    },
    // Taller image slot to match the reference — height fixed at 100px
    // (per the Figma dev-mode spec), width left auto so the actual asset's
    // aspect ratio isn't distorted; object-fit keeps it fully contained.
    badgeImageWrapper: {
        css: css`
            height: 64px;
            width: 72px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: visible;
            position: relative;
            margin-top: -5px;
            margin-bottom: -5px;

            img {
                display: block;
                width: auto;
                height: 64px;
                max-width: 72px;
                object-fit: contain;
            }
        `,
    },
    // Description + CTA render as a full-width block below the
    // image/title/badge row — NOT indented under the text column —
    // so it lines up flush with the left edge of the logo image.
    descriptionWrapper: {
        css: css`
            margin-top: 24px;
            margin-left: 20px;
            @media (max-width: 786px) {
                margin-left: 0px;
            }
            @media (min-width: 820px) and (max-width: 1180px) {
                margin-left: 0px;
            }

            p {
                font-size: 16px;
                line-height: 24px;
                font-weight: 400;
            }
        `,
    },
    contentWrapper: {
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 22px;
            color: #4a4a4a;
            // margin-bottom: 16px;

            a {
                color: rgb(15, 150, 217);
                text-decoration: none;
            }
        `,
    },
    linkStyles: {
        css: css`
            display: inline-block;
            // margin-bottom: 16px;
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                color: rgb(15, 150, 217);
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 16px;
            }
        `,
    },
    jumpToCategories: {
        css: css`
            font-family: Poppins;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            justify-content: center;
            @media (max-width: 768px) {
                margin-top: 20px;
            }

            & .flex-wrap {
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
            }
            &:hover svg path {
                fill: hsl(199.89999999999998, 87.1%, 31.8%);
            }
            &:focus {
                outline-color: none !important;
                outline-style: none !important;
                outline-width: none !important;
            }
        `,
    },
    brandContentContainerStyles: {
        css: css`
            // margin-left: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                    `,
                    css`
                        margin-left: 0px;
                    `,
                    css`
                        margin-left: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    brandGridContainerStyles: {
        css: css`
            div[class^="GridOffset"] {
                flex-wrap: nowrap;
                gap: 24px;
                @media (max-width: 786px) {
                    flex-direction: column;
                }
            }
        `,
    },
    brandSubheadingStyles: {
        css: css`
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: ${COLORS.black};
        `,
    },
    brandImageGridItem: {
        // width: [12, 12, 5, 3, 3],
        css: css`
            width: auto !important;
            max-width: none !important;
            flex-basis: auto !important;
            padding-right: 0px !important;
            padding-top: 0px !important;
            @media (max-width: 786px) {
                justify-content: center;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                    css`
                        margin-right: 80px;
                    `,
                ])}
        `,
    },
    imageWrapper: {
        css: css`
            max-height: 185px;
            height: 185px;
            aspect-ratio: 1/1;
            border: 1px solid #9e9d9e;
            margin-left: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 0px;
                    `,
                    css`
                        margin-left: 0px;
                    `,
                    css`
                        margin-left: 0px;
                    `,
                    null,
                    null,
                ])}

            @media (min-width: 992px) and (max-width: 1200px) {
                margin-left: 0px;
            }

            > div {
                width: 100%;
                height: 100%;

                > img {
                    width: 100%;
                    height: auto;
                    object-fit: contain;
                    aspect-ratio: 1/1;
                }
            }
        `,
    },
    brandContentGridItem: {
        // width: [12, 12, 7, 9, 9],
        css: css`
            flex-direction: column;
            width: auto !important;
            max-width: none !important;
            flex-basis: auto !important;
            padding: 0px !important;
            @media (max-width: 768px) {
                padding: 15px !important;
            }

            h1 {
                font-size: 32px !important;
                line-height: 42px;
                margin-bottom: 4px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        h1 {
                            font-size: 50px !important;
                        }
                    `,
                    css`
                        h1 {
                            font-size: 50px !important;
                        }
                    `,
                    css`
                        h1 {
                            font-size: 50px !important;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    buttonGridItem: {
        css: css`
            display: flex;
            margin-top: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    null,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    brandName: {
        css: css`
            margin-bottom: 8px;
            margin-left: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: 41px;
                        line-height: 50px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    button: {
        css: css`
            cursor: pointer;
            font-family: inherit;
            line-height: 1;
            backface-visibility: hidden;
            word-break: keep-all;
            border: 2px solid rgb(143, 4, 70);
            background: transparent;
            color: rgb(143, 4, 70);
            border-radius: 24px;
            min-width: 125px;
            display: inline-flex;
            -webkit-box-align: center;
            align-items: center;
            -webkit-box-pack: center;
            justify-content: center;
            transition: 100ms ease-out;
            margin-right: 24px;
            padding: 3px 16px;
            min-height: unset !important;
            height: auto !important;
            &:focus {
                outline: 0 !important;
            }

            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 24px;
                color: rgb(143, 4, 70) !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px 10px;
                        border-radius: 34px;
                    `,
                    null,
                    css`
                        border-radius: 45px;
                    `,
                    css``,
                    null,
                ])}
        `,
    },
    extButton: {
        css: css`
            border: 2px solid yellow;
            background: ${COLORS.secondary.base};
            border: 2px solid ${COLORS.secondary.base};
            border-radius: 24px;
            min-height: 48px;
            height: 32px;
            min-width: 125px;
            padding: 13px 24px;
            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                color: ${COLORS.white} !important;
                font-size: ${TYPOGRAPHY.size.large};
                line-height: 24px;
            }
        `,
    },
};

const styles = logoStyles;

// Small inline icons so this file doesn't depend on icons that may not
// exist in @tCentro/Icons — swap for your real icon set if you have one.
const CheckmarkIcon: FC = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="8" fill="#1C8A4C" />
        <path
            d="M4.5 8.2L6.8 10.5L11.5 5.5"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const PartsIcon: FC = () => (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="28" height="28" rx="4" fill="#EDEDED" />
        <circle cx="11" cy="14" r="3.5" stroke="#8A8A8A" strokeWidth="1.4" />
        <path d="M17 10L21 14L17 18" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Renders the badge image — the CMS field if an editor set one, otherwise
// the default distributor-badge graphic. Uses a plain <img> (not LazyImage)
// so we can (a) attach an error handler that falls back to the icon if the
// URL 404s, and (b) avoid LazyImage's own aspect-ratio placeholder box,
// which was making this taller than the green background.
const BadgeImage: FC<{ imageSrc?: string; altText: string }> = ({ imageSrc, altText }) => {
    const [hasError, setHasError] = useState(false);
    const src = imageSrc || DEFAULT_BADGE_IMAGE;

    return (
        <StyledWrapper {...styles.badgeImageWrapper}>
            <img src={src} alt={altText} onError={() => setHasError(true)} />
        </StyledWrapper>
    );
};

const BrandDetailsLogo: FC<Props> = ({ fields, isBrandDetailsCategoriesPresent }) => {
    const history = useHistory();
    const { value: brand } = useContext(BrandStateContext);
    const { url } = useGetLink(fields.getInTouchUrl);
    const { isInShell } = useShellContext();
    const [truncated, setTruncated] = useState(true);
    const [isIntraPageNavPresent, setIsIntraPageNavPresent] = React.useState(false);
    const [isInfoSectionPresent, setIsInfoSectionPresent] = React.useState(false);

    React.useEffect(() => {
        const infoSectionEl = document.querySelector('[data-test-selector="brandDetailsInfoSection"]');
        setIsInfoSectionPresent(!!infoSectionEl);
    }, []);
    React.useEffect(() => {
        const intraPageNavEl = document.querySelector(".cen-brand-intra-page-navigation");
        setIsIntraPageNavPresent(!!intraPageNavEl);
    }, []);

    if (!brand) {
        return null;
    }

    const handleShopAll = (e: React.MouseEvent) => {
        history.push(brand.productListPagePath);
        e.preventDefault();
    };

    const handleGetInTouch = (e: React.MouseEvent) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    const handleLearnMoreClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const infoSectionEl = document.querySelector(
            '[data-test-selector="brandDetailsInfoSection"]',
        ) as HTMLElement | null;
        if (infoSectionEl) {
            const header = document.querySelector('[data-test-selector="header"]') as HTMLElement | null;
            const headerHeight = header ? header.offsetHeight : 165;
            const yOffset = -(headerHeight + 20);
            const y = infoSectionEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const handleJumpToCategories = (e: React.MouseEvent) => {
        e.preventDefault();
        const categoriesEl = document.querySelector(
            '[data-test-selector="brandCategoriesTitle"]',
        ) as HTMLElement | null;
        if (categoriesEl) {
            const header = document.querySelector('[data-test-selector="header"]') as HTMLElement | null;
            const headerHeight = header ? header.offsetHeight : 165;
            const yOffset = -(headerHeight + 20);
            const y = categoriesEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const showCta =
        brand.properties.hasProducts !== "false" &&
        brand.properties.hasProducts !== undefined &&
        !fields.getInTouchCheckbox;

    const ctaHandler = showCta ? handleShopAll : fields.getInTouchCheckbox ? handleGetInTouch : undefined;
    const ctaLabel = showCta ? translate("Explore {0} Products").replace("{0}", brand.name) : translate("Get In Touch");
    const brandDetailContent = translate("Learn More About {0} ").replace("{0}", brand.name);

    const badgeLabel = siteMessage("Official Distributor");
    const badgeDetail = fields.badgeDetail
        ? fields.badgeDetail.replace("{0}", brand.name)
        : translate("Configurable {0} Parts").replace("{0}", brand.name);

    const hasLongContent = !!brand.htmlContent && brand.htmlContent.length > CONTENT_TRUNCATE_LENGTH;
    const displayedHtml =
        brand.htmlContent && !getIsWebCrawler() && truncated
            ? truncate(brand.htmlContent, { length: CONTENT_TRUNCATE_LENGTH })
            : brand.htmlContent;

    return (
        <StyledWrapper data-test-selector="brandDetailsLogo" {...styles.brandContentWrapper}>
            <GridContainer {...styles.brandGridContainerStyles}>
                <GridItem {...styles.brandImageGridItem}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage src={brand.logoLargeImagePath} altText={brand.name} />
                    </StyledWrapper>
                </GridItem>

                <GridItem {...styles.brandContentGridItem}>
                    {/* Only the title, subheading and badge live next to the image.
                        The description and CTA render as a separate full-width block below. */}
                    <StyledWrapper {...styles.brandContentContainerStyles}>
                        <Typography variant="h1" {...styles.brandName}>
                            {brand.name}
                        </Typography>

                        {brand.properties.brands_SubHeading && (
                            <Typography as="h2" {...styles.brandSubheadingStyles}>
                                {brand.properties.brands_SubHeading}
                            </Typography>
                        )}

                        {(brand.properties.isOfficialDistributor === "true" || brand.properties.catalogCountLabel) && (
                            <StyledWrapper {...styles.badgeWrapper}>
                                <BadgeImage imageSrc={fields.badgeImage} altText={brand.name} />
                                <span className="badge-text">
                                    {brand.properties.isOfficialDistributor === "true" && (
                                        <span className="badge-label">
                                            <CheckmarkIcon />
                                            {badgeLabel}
                                        </span>
                                    )}
                                    {brand.properties.catalogCountLabel && (
                                        <span className="badge-detail">{brand.properties.catalogCountLabel}</span>
                                    )}
                                </span>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>

            {/* Full-width description + CTA, flush with the image's left edge */}
            <StyledWrapper {...styles.descriptionWrapper}>
                {brand.htmlContent && (
                    <>
                        <StyledWrapper
                            data-test-selector="brandContent"
                            {...(truncated ? styles.clampedContentCss : styles.contentWrapper)}
                        >
                            {parse(transformContentHtml(displayedHtml), parserOptions)}
                        </StyledWrapper>

                        {hasLongContent && !getIsWebCrawler() && !isInfoSectionPresent && (
                            <Link
                                href=""
                                onClick={e => {
                                    e.preventDefault();
                                    setTruncated(!truncated);
                                }}
                                {...styles.linkStyles}
                            >
                                {truncated ? siteMessage("Show More") : siteMessage("Show Less")}
                            </Link>
                        )}
                        {hasLongContent && !getIsWebCrawler() && isInfoSectionPresent && (
                            <Link href="#brand-details-information" onClick={handleLearnMoreClick} {...styles.linkStyles}>
                                {brandDetailContent}
                            </Link>
                        )}
                    </>
                )}

                {ctaHandler && (
                    <StyledWrapper {...styles.buttonGridItem}>

                        <Link href={brand.productListPagePath} {...styles.button}>
                            {ctaLabel}
                        </Link>

                        {showCta && isBrandDetailsCategoriesPresent && !isIntraPageNavPresent && (
                            <Link {...styles.jumpToCategories} onClick={handleJumpToCategories}>
                                <span className="flex-wrap">
                                    <span>{siteMessage("Jump to Product Categories")}</span>
                                    <ArrowDownIcon />
                                </span>
                            </Link>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(BrandDetailsLogo),
    definition: {
        group: "Brand Details",
        icon: "image-landscape",
        displayName: "CEN Brand Details Overview",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.getInTouchCheckbox,
                displayName: "Show Get In Touch",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.getInTouchUrl,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: (item: HasFields) => item.fields.getInTouchCheckbox === true,
            },
            {
                // Optional per-brand override. Leave blank to use the
                // bundled default badge graphic.
                name: fields.badgeImage,
                displayName: "Badge Image (optional override)",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
